/**
 * Phase 11 — Admin Launch Bridge Checklist page.
 * Read-only diagnostic. Renders whether env values are configured and
 * lists the exact URLs the WordPress/PMPro site should be pointed at.
 * Never displays secret values.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plug, CheckCircle2, AlertCircle, Copy, Link as LinkIcon } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card,
  border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card,
  padding: 20,
};

const CopyRow = ({ label, value, testid }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
    <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text.muted,
      textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <code data-testid={testid} style={{ flex: 1, padding: '8px 10px',
        background: theme.colors.bg.secondary, borderRadius: theme.radius.sm,
        border: `1px solid ${theme.colors.borders}`, fontSize: 12,
        color: theme.colors.text.primary, wordBreak: 'break-all' }}>
        {value || '—'}
      </code>
      {value && (
        <button data-testid={`${testid}-copy`}
          onClick={() => { navigator.clipboard.writeText(value); toast.success('Copied'); }}
          style={{ padding: '6px 8px', borderRadius: theme.radius.sm,
            border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
            cursor: 'pointer' }}>
          <Copy size={12} />
        </button>
      )}
    </div>
  </div>
);

export default function AdminLaunchBridgePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/launch-bridge/checklist`, auth);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load checklist');
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div data-testid="lb-loading" style={{ padding: 40, color: theme.colors.text.muted }}>Loading…</div>;
  if (!data) return <div data-testid="lb-empty" style={{ padding: 40 }}>No data</div>;

  const allOk = (data.warnings || []).length === 0
    && (data.checks || []).every(c => c.ok);

  return (
    <div data-testid="admin-launch-bridge-page" style={{ padding: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Plug size={24} color={theme.colors.primary} />
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0,
          fontFamily: theme.fonts.heading, color: theme.colors.text.primary }}>
          Launch Bridge Checklist
        </h1>
      </div>
      <p style={{ fontSize: 13, color: theme.colors.text.secondary, marginBottom: 20 }}>
        Point the WordPress/PMPro site at the URLs below. The webhook secret
        must match the backend&apos;s <b>PMPRO_WEBHOOK_SECRET</b> env value
        (never shown here).
      </p>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {allOk
            ? <CheckCircle2 size={18} color={theme.colors.status.success} />
            : <AlertCircle  size={18} color={theme.colors.status.warning} />}
          <div data-testid="lb-status-summary"
            style={{ fontWeight: 800, color: allOk
              ? theme.colors.status.success : theme.colors.status.warning }}>
            {allOk ? 'All checks passed. Ready to connect.' : 'Configuration warnings detected.'}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 16, marginBottom: 16 }}>
        {(data.checks || []).map(c => (
          <div key={c.key} data-testid={`lb-check-${c.key}`} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {c.ok
                ? <CheckCircle2 size={16} color={theme.colors.status.success} />
                : <AlertCircle  size={16} color={theme.colors.status.error} />}
              <div style={{ fontWeight: 700, fontSize: 13 }}>{c.key}</div>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: theme.colors.text.muted }}>
              {c.description}
            </div>
          </div>
        ))}
      </div>

      {(data.warnings || []).length > 0 && (
        <div data-testid="lb-warnings" style={{ ...cardStyle, marginBottom: 16,
          background: '#FFFBEB', borderColor: '#FDE68A' }}>
          <div style={{ fontWeight: 800, color: '#78350F', marginBottom: 6 }}>Warnings</div>
          {(data.warnings || []).map((w, i) => (
            <div key={i} style={{ fontSize: 12, color: '#78350F' }}>· {w}</div>
          ))}
        </div>
      )}

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <LinkIcon size={16} color={theme.colors.primary} />
          <div style={{ fontWeight: 800, fontSize: 15 }}>WordPress → App URLs</div>
        </div>
        <CopyRow label="PMPro Webhook URL" value={data.webhook_url} testid="lb-webhook-url" />
        <CopyRow label="Post-payment URL Pattern" value={data.post_payment_url_pattern} testid="lb-postpay-url" />
        <CopyRow label="App Login" value={data.login_url} testid="lb-login-url" />
        <CopyRow label="Enterprise Inquiry" value={data.enterprise_inquiry_url} testid="lb-enterprise-url" />
      </div>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10 }}>PMPro Level → App Plan</div>
        <div style={{ display: 'grid', gap: 6 }}>
          {Object.entries(data.plan_level_name_to_plan_key || {}).map(([levelName, planKey]) => (
            <div key={levelName} data-testid={`lb-plan-map-${planKey}`}
              style={{ display: 'flex', justifyContent: 'space-between',
                padding: '4px 8px', background: theme.colors.bg.secondary,
                borderRadius: 4, fontSize: 12 }}>
              <span>{levelName}</span>
              <code style={{ color: theme.colors.primary }}>{planKey}</code>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: theme.colors.text.muted,
          fontStyle: 'italic' }}>
          {data.enterprise_manual_only && 'Enterprise is manual-review only — never auto-activated.'}
        </div>
      </div>

      <div style={{ ...cardStyle }}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10 }}>WordPress Setup</div>
        <ol style={{ fontSize: 12, color: theme.colors.text.primary, paddingLeft: 20, lineHeight: 1.6 }}>
          <li>Set <b>PMPRO_WEBHOOK_SECRET</b> on the backend .env to the same
            value you will use on the WordPress side.</li>
          <li>Configure PMPro to POST membership events to <b>{data.webhook_url}</b>
            with header <code>X-Kickback-Webhook-Secret</code> set to that
            shared secret (or an HMAC-SHA256 signature via
            <code> X-Kickback-Signature</code>).</li>
          <li>On payment success, redirect the buyer to the <b>post_payment_url</b>
            returned by the webhook response (pattern:
            <b> {data.post_payment_url_pattern}</b>).</li>
          <li>Send <code>event_id</code>, <code>transaction_id</code>, or
            <code> order_id</code> in the payload for idempotent retries.</li>
          <li>Do NOT enable public checkout for Business Enterprise — send
            those leads to <b>{data.enterprise_inquiry_url}</b>.</li>
        </ol>
      </div>
    </div>
  );
}
